import { useTranslation } from 'react-i18next'
import { HiOutlineMail, HiOutlineTrash } from 'react-icons/hi'

import {
  AccordionPanel,
  Badge,
  Box,
  Center,
  Flex,
  Grid,
  Icon,
  Spinner,
  Tag,
  TagLabel,
  Tooltip,
  Wrap,
} from '@chakra-ui/react'

import { SettingsRow } from '@/components/ui'
import { removeUnderscoreAndCapitalize } from '@/utils/formats'

import { PendingInviteeIProps } from '../types/types'

interface PendingInviteeComponentIProps {
  index: number
  invitee: PendingInviteeIProps
  isResending?: boolean
  onResend: (invitee: PendingInviteeIProps) => void
  onRemove: (invitee: PendingInviteeIProps) => void
}

export const PendingInvitee = ({
  index,
  invitee,
  isResending,
  onResend,
  onRemove,
}: PendingInviteeComponentIProps) => {
  const { t } = useTranslation()
  const {
    firstName,
    lastName,
    email,
    roles,
    invitedAt,
    lastInviteSentAt,
    isExpired,
  } = invitee
  const fullName = `${firstName} ${lastName}`
  const invitedDate = new Date(invitedAt).toLocaleDateString()
  const lastResentDate = lastInviteSentAt
    ? new Date(lastInviteSentAt).toLocaleDateString()
    : null

  return (
    <AccordionPanel
      bgColor={index % 2 === 0 ? 'transparent' : 'white'}
      borderTop='1px solid #eaedf3'
      p='0'
    >
      <Flex alignItems='center' justifyContent='space-between' px='4' py='6'>
        <Grid columnGap='6' gridTemplateColumns='auto 1fr' rowGap='4' w='full'>
          <SettingsRow
            dataTestId='PendingInviteeRow:name'
            label={t('settings:pending_invitees_name_label')}
            value={fullName}
          />
          <SettingsRow
            dataTestId='PendingInviteeRow:email'
            label={t('settings:pending_invitees_email_label')}
            value={email}
          />
          <Box color='gray.500' fontSize='sm' fontWeight='semibold'>
            {t('settings:pending_invitees_roles_label')}
          </Box>
          <Wrap data-testid='PendingInviteeRow:roles' spacing={2}>
            {roles.map((role) => (
              <Tag
                borderRadius='full'
                colorScheme='blue'
                key={role}
                size='sm'
                variant='subtle'
              >
                <TagLabel>{removeUnderscoreAndCapitalize(role)}</TagLabel>
              </Tag>
            ))}
          </Wrap>
          <Box color='gray.500' fontSize='sm' fontWeight='semibold'>
            {t('settings:pending_invitees_status_label')}
          </Box>
          <Box data-testid='PendingInviteeRow:status'>
            <Badge
              borderRadius='full'
              colorScheme={isExpired ? 'red' : 'green'}
              fontSize='xs'
              px={2}
              py={0.5}
            >
              {isExpired
                ? t('settings:pending_invitees_status_expired')
                : t('settings:pending_invitees_status_active')}
            </Badge>
          </Box>
          <SettingsRow
            dataTestId='PendingInviteeRow:invited'
            label={t('settings:pending_invitees_invited_label')}
            value={invitedDate}
          />
          <SettingsRow
            dataTestId='PendingInviteeRow:lastResent'
            label={t('settings:pending_invitees_last_resent_label')}
            value={
              lastResentDate || t('settings:pending_invitees_last_resent_never')
            }
          />
        </Grid>
        <Box height='90px' ml='10px'>
          <Grid gap='10px' gridTemplateRows='1fr 1fr' h='full' maxW='100%'>
            <Tooltip
              hasArrow
              label={t('settings:pending_invitees_resend_tooltip')}
              placement='left'
            >
              <Center
                _hover={{ bgColor: isResending ? 'blue.500' : 'blue.600' }}
                aria-label={t('settings:pending_invitees_resend_tooltip')}
                as='button'
                bgColor='blue.500'
                boxShadow='0px 2px 6px 2px rgba(0, 0, 0, 0.1)'
                cursor={isResending ? 'not-allowed' : 'pointer'}
                data-testid='PendingInviteeRow:resendButton'
                disabled={isResending}
                h='40px'
                onClick={() => onResend(invitee)}
                opacity={isResending ? 0.7 : 1}
                rounded='5px'
                transition='ease-in-out'
                transitionDuration='300ms'
                transitionProperty='background-color'
                type='button'
                w='60px'
              >
                {isResending ? (
                  <Spinner color='white' size='sm' />
                ) : (
                  <Icon as={HiOutlineMail} boxSize='24px' color='white' />
                )}
              </Center>
            </Tooltip>
            <Tooltip
              hasArrow
              label={t('settings:pending_invitees_remove_tooltip')}
              placement='left'
            >
              <Center
                _hover={{ bgColor: 'red.600' }}
                aria-label={t('settings:pending_invitees_remove_tooltip')}
                as='button'
                bgColor='red.500'
                boxShadow='0px 2px 6px 2px rgba(0, 0, 0, 0.1)'
                cursor='pointer'
                data-testid='PendingInviteeRow:removeButton'
                h='40px'
                onClick={() => onRemove(invitee)}
                rounded='5px'
                transition='ease-in-out'
                transitionDuration='300ms'
                transitionProperty='background-color'
                type='button'
                w='60px'
              >
                <Icon as={HiOutlineTrash} boxSize='24px' color='white' />
              </Center>
            </Tooltip>
          </Grid>
        </Box>
      </Flex>
    </AccordionPanel>
  )
}
